import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import Input from "../../components/controls/input/customInput/CustomInput";
import InputUnderline from "../../components/controls/input/InputUnderline";
import Modal from "../../components/controls/modal/Modal";
import Searchable from "../../components/controls/select/CustomSelect";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import { getLabelByType, taskPriorityOptions, taskStatusOptions } from "../../helpers/utils";
import { generalActions } from "../../store/actions";
import searchService from "../../store/services/search.service";
import taskGroupService from "../../store/services/taskGroup.service";

// Small create/edit modal for TaskGroup, modeled on Roles.jsx's minimal
// formBody() + generic Modal pattern rather than the full CreateEditTask form.
// Scope is only editable in creation mode (requireScopePicker); when the
// scope is already implicit (edit, or the scope-overview Tasks tab) no scope
// field is rendered at all.
const CreateEditTaskGroup = ({
  taskGroup,
  isEdit,
  scopeId,
  requireScopePicker = false,
  portfolioId,
  programId,
  projectId,
  initialTasks = [],
  isOpen,
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [refId, setRefId] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(0);
  const [priority, setPriority] = useState(0);
  const [phase, setPhase] = useState("");
  const [scope, setScope] = useState(null);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [taskSearchOptions, setTaskSearchOptions] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState(initialTasks);
  const [taskPickerKey, setTaskPickerKey] = useState(0);
  const [formErrors, setFormErrors] = useState({});

  const effectiveScopeId = requireScopePicker ? scope?.value : scopeId;

  useEffect(() => {
    if (!isEdit || !taskGroup?.id) return;
    setIsLoading(true);
    taskGroupService
      .getTaskGroupDetailsRequest({ id: taskGroup.id })
      .then((detail) => {
        setName(detail.name);
        setRefId(detail.refId || "");
        setDescription(detail.description || "");
        setStatus(detail.status ?? 0);
        setPriority(detail.priority ?? 0);
        setPhase(detail.phase || "");
        setSelectedTasks((detail.tasks || []).map((t) => ({ id: t.id, name: t.name })));
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => setIsLoading(false));
  }, [isEdit, taskGroup?.id]);

  const searchScopeHandler = (value) => {
    searchService
      .Scopes(value, { portfolioId, programId, projectId })
      .then((res) => {
        setScopeOptions((res || []).map((obj) => ({ label: obj.name, value: obj.id })));
      })
      .catch(() => setScopeOptions([]));
  };

  const searchTaskHandler = (value) => {
    if (!effectiveScopeId) {
      setTaskSearchOptions([]);
      return;
    }
    searchService
      .Tasks(value, { scopeId: effectiveScopeId })
      .then((res) => {
        const selectedIds = new Set(selectedTasks.map((t) => t.id));
        setTaskSearchOptions(
          (res || [])
            .filter((t) => !selectedIds.has(t.id))
            .map((t) => ({ label: t.name, value: t.id }))
        );
      })
      .catch(() => setTaskSearchOptions([]));
  };

  const handleAddTask = (option) => {
    if (!option) return;
    setSelectedTasks((prev) =>
      prev.some((t) => t.id === option.value)
        ? prev
        : [...prev, { id: option.value, name: option.label }]
    );
    setTaskSearchOptions([]);
    // Remount the picker so it clears back to an empty "add another" box
    // instead of continuing to display the option that was just picked.
    setTaskPickerKey((prev) => prev + 1);
  };

  const handleRemoveTask = (taskId) => {
    setSelectedTasks((prev) => prev.filter((t) => t.id !== taskId));
  };

  const handleScopeChange = (val) => {
    setScope(val);
    // Tasks belong to the scope they were attached under; a different scope
    // means the previous selection no longer applies.
    setSelectedTasks([]);
    setTaskSearchOptions([]);
    setTaskPickerKey((prev) => prev + 1);
  };

  const handleClose = () => {
    setFormErrors({});
    onClose?.();
  };

  const handleSave = () => {
    const errors = {};
    if (!name) errors.name = "The Name field is required.";
    if (requireScopePicker && !scope) errors.scope = "The Scope field is required.";
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    setIsLoading(true);
    const taskIds = selectedTasks.map((t) => t.id);
    const commonFields = {
      name,
      refId: refId || null,
      description: description || "",
      status,
      priority,
      phase: phase || null,
      taskIds,
    };

    const savePromise = isEdit
      ? taskGroupService.updateTaskGroupRequest({
        id: taskGroup.id,
        payload: commonFields,
      })
      : taskGroupService.createTaskGroupRequest({
        payload: { ...commonFields, scopeId: effectiveScopeId },
      });

    savePromise
      .then(() => {
        dispatch(
          generalActions.showToast({
            title: `Task group ${isEdit ? "updated" : "created"} successfully`,
            type: "success",
          })
        );
        onSaved?.(effectiveScopeId);
        handleClose();
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => setIsLoading(false));
  };

  const taskPicker = (
    <Searchable
      key={taskPickerKey}
      label="Tasks"
      SelectText={effectiveScopeId ? "tasks to attach" : "a scope first"}
      searchText={searchTaskHandler}
      options={taskSearchOptions}
      defaultVal={null}
      onChange={handleAddTask}
      isDisabled={!effectiveScopeId}
      loading={isLoading}
    />
  );

  const formBody = () => (
    <>
      <div className="d-flex align-items-start w-100 mb-3">
        <InputUnderline
          inputUnderlinewd="w-100"
          isRequired
          placeholder="Task group name"
          value={name}
          handleChange={setName}
          error={formErrors.name}
          loading={isLoading}
        />
      </div>

      <div className="row align-items-center gx-3 mb-3">
        <div className="col-3">
          <Input
            icon={false}
            placeholder="Enter Reference ID"
            value={refId}
            type="text"
            handleChange={setRefId}
            label="Reference ID"
            loading={isLoading}
          />
        </div>
        <div className="col-3">
          <DropDown
            label="Priority"
            options={taskPriorityOptions}
            defaultVal={getLabelByType("taskPriority", priority)}
            onChange={setPriority}
            loading={isLoading}
          />
        </div>
        <div className="col-3">
          <DropDown
            label="Status"
            options={taskStatusOptions}
            defaultVal={getLabelByType("taskStatus", status)}
            onChange={setStatus}
            loading={isLoading}
          />
        </div>
        <div className="col-3">
          <Input
            icon={false}
            placeholder="Enter phase"
            value={phase}
            type="text"
            handleChange={setPhase}
            label="Phase"
            loading={isLoading}
          />
        </div>
      </div>

      {requireScopePicker ? (
        <div className="row align-items-start gx-3 mb-2">
          <div className="col-6">
            <Searchable
              label="Scope"
              SelectText="Scope"
              searchText={searchScopeHandler}
              options={scopeOptions}
              defaultVal={scope}
              onChange={handleScopeChange}
              errorMessage={formErrors.scope}
              isRequired
              loading={isLoading}
            />
          </div>
          <div className="col-6">{taskPicker}</div>
        </div>
      ) : (
        <div className="d-flex align-items-start w-100 mb-4">{taskPicker}</div>
      )}

      {selectedTasks.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-4 w-100">
          {selectedTasks.map((task) => (
            <span
              key={task.id}
              className="d-flex align-items-center text-sm-400"
              style={{
                background: "#ECE7FD",
                color: "#342C82",
                borderRadius: "14px",
                padding: "4px 8px 4px 12px",
                gap: "6px",
              }}
            >
              {task.name}
              <X
                size={13}
                style={{ cursor: "pointer" }}
                onClick={() => handleRemoveTask(task.id)}
              />
            </span>
          ))}
        </div>
      )}

      <div className="d-flex align-items-start w-100">
        <RichTextEditor
          label="Description"
          content={description}
          onChange={setDescription}
          loading={isLoading}
        />
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <Modal
      title={isEdit ? "Edit Task Group" : "Create Task Group"}
      handleCancel={handleClose}
      handleSave={handleSave}
      saveBtnLabel="Save"
      cancelBtnLabel="Cancel"
      isLoading={isLoading}
      body={formBody()}
      size="large"
      width="700px"
    />
  );
};

export default CreateEditTaskGroup;
